import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { RiCheckboxCircleLine } from '@remixicon/react';
import { useState } from 'react';
import useAuthStore from '../../stores/authStore';
import useSubscriptionStore from '../../stores/subscriptionStore';
import { useNavigate } from 'react-router-dom';
import SpinnerButton from '../../components/Button/SpinnerButton';
import { authFetch } from '../../utils/apiClient.js';
import useProductStore from '../../stores/productStore.js';

function PricingScreen() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingPriceId, setLoadingPriceId] = useState(null);
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const { subscription } = useSubscriptionStore();
    const { products, currentProduct } = useProductStore();

    const plans = products.map((product) => {
        const metadata = product.metadata;
        let features = [];
        let highlighted = false;
        if (metadata) {
            features = [
                { key: 'generations', value: metadata.limit || 0 },
                { key: 'product', value: (metadata.product === "1") || false },
                { key: 'article', value: (metadata.article === "1") || false },
                { key: 'image', value: (metadata.image === "1") || false },
                { key: 'legal', value: (metadata.legal === "1") || false },
                { key: 'support', value: (metadata.support === "1") || false }
            ];
            highlighted = (metadata.highlighted === '1') || false;
        }
        return {
            name: product.name.toUpperCase(),
            price: product.amount,
            priceId: product.priceId,
            features,
            highlighted,
        };
    });

    const featureLabels = {
        generations: t('pricing.generations'),
        product: t('pricing.product'),
        article: t('pricing.article'),
        image: t('pricing.image'),
        legal: t('pricing.legal'),
        support: t('pricing.support')
    };

    const handleCheckout = async (priceId) => {
        setIsLoading(true);
        setLoadingPriceId(priceId);
        const params = new URLSearchParams(window.location.search);
        const affiliateId = params.get('ref');
        try {
            const response = await authFetch('/api/subscriptions/checkout', {
                method: 'POST',
                body: JSON.stringify({
                    priceId,
                    successUrl: window.location.origin + '/pricing/success',
                    cancelUrl: window.location.origin + '/pricing',
                    affiliateId: affiliateId
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la création de la session de paiement');
            }

            const data = await response.json();

            if (data.sessionUrl) {
                window.location.href = data.sessionUrl;
            }
        } catch (error) {
            console.error('Erreur checkout:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpgrade = async (priceId) => {
        setIsLoading(true);
        setLoadingPriceId(priceId);
        try {
            const response = await authFetch('/api/subscriptions/upgrade', {
                method: 'POST',
                body: JSON.stringify({
                    subscriptionId: subscription.id,
                    newPriceId: priceId
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la mise à jour de l\'abonnement');
            }
        } catch (error) {
            console.error('Erreur upgrade:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const isUserSubscribedToPlan = (priceId) => {
        return currentProduct?.priceId === priceId;
    };

    const renderFeature = (feature) => {
        if (feature.key === 'generations') {
            return (
                <div className="d-flex align-items-center mb-2">
                    <RiCheckboxCircleLine size={18} className="me-2 pink-color" />
                    <span>{feature.value} {featureLabels[feature.key]}</span>
                </div>
            );
        }

        if (feature.key === 'support') {
            return (
                <div className="d-flex align-items-center mb-2">
                    <RiCheckboxCircleLine size={18} className="me-2 pink-color" />
                    <span>
                        {featureLabels[feature.key]} {feature.value === true ? t('pricing.priority') : t('pricing.standard')}
                    </span>
                </div>
            );
        }

        if (feature.value === true) {
            return (
                <div className="d-flex align-items-center mb-2">
                    <RiCheckboxCircleLine size={18} className="me-2 pink-color" />
                    <span>{featureLabels[feature.key]}</span>
                </div>
            );
        }

        return null;
    };

    const handleButtonClick = (priceId) => {
        if (!user || !user.emailVerified) {
            const returnUrl = window.location.pathname + window.location.search;
            sessionStorage.setItem('returnUrl', returnUrl);
            navigate("/login");
            return;
        }
        subscription ? handleUpgrade(priceId) : handleCheckout(priceId);
    };

    return (
        <Container className="py-5">
            <Row className="mb-5">
                <Col xs={12} className="text-center">
                    <h2 className="title mb-2">{t('pricing.title')}</h2>
                    <p className="subtitle">{t('pricing.subtitle')}</p>
                </Col>
            </Row>

            <Row className="g-4">
                {plans.map((plan, index) => (
                    <Col xs={12} md={6} lg={4} key={index} className="d-flex">
                        <div className={`card-custom-content w-100 d-flex flex-column ${plan.highlighted ? 'border border-primary border-2' : ''}`} style={{ minHeight: '600px' }}>
                            <div className="text-center pb-4 mb-4 border-bottom">
                                <h3 className="fw-bold">{plan.name}</h3>
                                <div className="mt-3">
                                    <span className="fs-4 fw-bold">{t('pricing.trialPeriod')}</span>
                                </div>
                                <small className="mt-2">{t('pricing.then')} {plan.price}€ {t('pricing.perMonth')}</small>
                            </div>

                            <div className="flex-grow-1 d-flex flex-column justify-content-between mb-4">
                                <div>
                                    {plan.features.map((feature, fIndex) => (
                                        <div key={fIndex}>
                                            {renderFeature(feature)}
                                        </div>
                                    ))}
                                </div>
                                <small className="fw-light">{t('pricing.cancelAnytime')}</small>
                            </div>

                            {(!user || !user.emailVerified || !isUserSubscribedToPlan(plan.priceId)) && (
                                <SpinnerButton
                                    variant={plan.highlighted ? 'primary' : 'outline-primary'}
                                    className={`${plan.highlighted ? 'bg-pink' : ''}`}
                                    onClick={() => handleButtonClick(plan.priceId)}
                                    isLoading={isLoading && loadingPriceId === plan.priceId}
                                    disabled={isLoading}
                                    label={t('pricing.choosePlan')}
                                />
                            )}

                        </div>
                    </Col>
                ))}
            </Row>
        </Container>
    );
}

export default PricingScreen;
